import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Heart, MessageCircle, ChevronRight, Star,
  DollarSign, TrendingUp, Quote, ArrowRight,
} from 'lucide-react';

// ─── People Who Get It ──────────────────────────────────────────────
// Not "top performers." Real people who were broke, tired, and
// struggling — and figured it out. Stories you can see yourself in.
// Because when you're at the bottom, you need to know it's possible.

interface Story {
  name: string;
  avatar: string;
  restaurant: string;
  city: string;
  monthsAgo: string;
  beforeIncome: string;
  afterIncome: string;
  quote: string;
  backstory: string;
  turningPoint: string;
  advice: string;
  details: { label: string; before: string; after: string }[];
}

const STORIES: Story[] = [
  {
    name: 'Marcus W.',
    avatar: 'MW',
    restaurant: 'Gen Korean BBQ',
    city: 'Los Angeles',
    monthsAgo: 'Started 8 months ago',
    beforeIncome: '$1,400/mo',
    afterIncome: '$2,850/mo',
    quote: 'I used to eat staff meals because I couldn\'t afford groceries. Now I\'m putting money into savings for the first time in my life.',
    backstory: 'Marcus was working doubles just to cover rent on a shared apartment in Koreatown. He\'d skip meals on days off to stretch his budget. He almost quit twice.',
    turningPoint: 'A coworker showed him which items had the highest margins and gave him a simple script for the Wagyu pitch. His first night trying it, he made $40 more than usual. "I remember thinking — that\'s a week of groceries from ONE shift."',
    advice: 'Don\'t try to change everything at once. Pick one item. Learn one script. Just say it to every table. That\'s it. You\'ll feel awkward the first three times and then it becomes automatic.',
    details: [
      { label: 'Avg shift earnings', before: '$95', after: '$203' },
      { label: 'Upsell rate', before: '12%', after: '72%' },
      { label: 'Monthly take-home', before: '$1,400', after: '$2,850' },
    ],
  },
  {
    name: 'Priya K.',
    avatar: 'PK',
    restaurant: 'SAJJ Mediterranean',
    city: 'San Jose',
    monthsAgo: 'Started 5 months ago',
    beforeIncome: '$1,100/mo',
    afterIncome: '$2,200/mo',
    quote: 'I was a single mom working two jobs. Now I only need one.',
    backstory: 'Priya was serving part-time at SAJJ while also working mornings at a daycare. She\'d drop her daughter off at 6 AM, work until 2, pick her up, then serve from 5-10. She was exhausted and barely breaking even.',
    turningPoint: 'Her manager told her she was already great with guests — she just wasn\'t asking for the upsell. "I thought upselling was being pushy. Then I realized: I\'m just telling them about the best stuff. If someone told ME about a great deal, I\'d want to know."',
    advice: 'Think of it like this: you\'re not selling anything. You\'re recommending something you\'d tell a friend about. If the food is actually good — and it is — you\'re doing them a favor.',
    details: [
      { label: 'Avg shift earnings', before: '$78', after: '$165' },
      { label: 'Jobs needed', before: '2', after: '1' },
      { label: 'Time with her daughter', before: '2 hrs/day', after: '6 hrs/day' },
    ],
  },
  {
    name: 'James T.',
    avatar: 'JT',
    restaurant: 'Shake Shack',
    city: 'Brooklyn',
    monthsAgo: 'Started 6 months ago',
    beforeIncome: '$1,250/mo',
    afterIncome: '$2,400/mo',
    quote: 'I was sleeping on my cousin\'s couch. Last month I signed a lease on my own place.',
    backstory: 'James moved to NYC with $300 and a job at Shake Shack. He\'d been couch-surfing for four months. He almost moved back home to Ohio.',
    turningPoint: 'He noticed the servers who were making the most money all did the same thing: they mentioned specific items by name instead of asking "anything else?" He started doing it and his tips jumped immediately. "It costs nothing to say \'the chocolate shake is insane today.\' But it made me an extra $20 a shift."',
    advice: 'Don\'t compare yourself to the people at the top of the leaderboard. Compare yourself to where YOU were last week. If you made $5 more, you won. Stack those $5 wins and they turn into rent money.',
    details: [
      { label: 'Avg shift earnings', before: '$82', after: '$171' },
      { label: 'Living situation', before: 'Couch surfing', after: 'Own apartment' },
      { label: 'Savings', before: '$0', after: '$1,200' },
    ],
  },
  {
    name: 'Rosa M.',
    avatar: 'RM',
    restaurant: 'Chipotle',
    city: 'Chicago',
    monthsAgo: 'Started 4 months ago',
    beforeIncome: '$980/mo',
    afterIncome: '$1,800/mo',
    quote: 'My kids don\'t have to share school supplies anymore.',
    backstory: 'Rosa had been at Chipotle for two years and was one of the hardest workers on the floor. But she was quiet with customers — head down, get the order, move on. Her manager kept telling her she could make more but she didn\'t know how.',
    turningPoint: 'She saw another server just casually say "want to add guac? It\'s so good today" and realized that was all it took. "I thought I had to give a whole speech. No. It\'s one sentence. That\'s it."',
    advice: 'You don\'t have to be outgoing. You don\'t have to have a personality. You just need one sentence. Practice it in the mirror once and then say it to every single person. That\'s the whole secret.',
    details: [
      { label: 'Avg shift earnings', before: '$62', after: '$128' },
      { label: 'Upsell attempts', before: '0/shift', after: '15/shift' },
      { label: 'Monthly take-home', before: '$980', after: '$1,800' },
    ],
  },
];

const PeopleWhoBelieve: React.FC = () => {
  const navigate = useNavigate();
  const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/restaurant/mockups')} className="flex items-center gap-1.5 text-gray-400 hover:text-gray-600 text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Hub
          </button>
          <h1 className="text-xl font-bold text-gray-900 mb-1">People Who Get It</h1>
          <p className="text-sm text-gray-500">
            Real servers. Real struggles. Real changes. These aren't the "top performers" —
            they're people who were exactly where you are right now.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {STORIES.map((story, idx) => {
          const isExpanded = expandedIdx === idx;
          return (
            <div key={idx} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Main card */}
              <button
                onClick={() => setExpandedIdx(isExpanded ? null : idx)}
                className="w-full text-left p-5 hover:bg-gray-50/50 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-500 flex-shrink-0">
                    {story.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-semibold text-gray-900">{story.name}</span>
                      <span className="text-xs text-gray-400">{story.restaurant} · {story.city}</span>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed mb-3 italic">
                      "{story.quote}"
                    </p>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm text-gray-400 line-through">{story.beforeIncome}</span>
                        <ArrowRight className="w-3 h-3 text-gray-300" />
                        <span className="text-sm font-bold text-emerald-600">{story.afterIncome}</span>
                      </div>
                      <span className="text-xs text-gray-400">{story.monthsAgo}</span>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-gray-300 flex-shrink-0 mt-1 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                </div>
              </button>

              {/* Expanded story */}
              {isExpanded && (
                <div className="border-t border-gray-100 px-5 py-5 space-y-4">
                  {/* Before/After stats */}
                  <div className="grid grid-cols-3 gap-3">
                    {story.details.map((d, i) => (
                      <div key={i} className="bg-gray-50 rounded-lg p-3 text-center">
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">{d.label}</div>
                        <div className="text-xs text-gray-400 line-through">{d.before}</div>
                        <div className="text-sm font-bold text-emerald-600">{d.after}</div>
                      </div>
                    ))}
                  </div>

                  {/* Their story */}
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Their story</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{story.backstory}</p>
                  </div>

                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">The turning point</h4>
                    <p className="text-sm text-gray-600 leading-relaxed">{story.turningPoint}</p>
                  </div>

                  {/* Their advice */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1.5">Their advice to you</h4>
                    <p className="text-sm text-amber-900 leading-relaxed">{story.advice}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Bottom message */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
          <Heart className="w-6 h-6 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-600 max-w-md mx-auto mb-2">
            Every one of these people was exactly where you are. Tired. Worried about money.
            Not sure if tomorrow would be better than today.
          </p>
          <p className="text-sm text-gray-900 font-medium">
            It got better for them. It can get better for you too.
          </p>
          <p className="text-xs text-gray-400 mt-3">
            The only difference between their "before" and "after" was one sentence, said to one table, on one shift.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PeopleWhoBelieve;
